import type { Node } from "@xyflow/react";
import {
    type BuildFacetGraphParams,
    type Cluster,
    type ExplodeFlow,
    type ExplodeNodeData,
    NodeRole,
} from "../../types";
import { buildFieldFocus } from "./buildFieldFocus";
import { buildFieldGrid } from "./buildFieldGrid";
import {
    COLUMN_GAP,
    GHOST_COUNT,
    GHOST_ZONE_SLOTS,
    graphNode,
    NODE_WIDTH,
    OVERLAY_Z,
    ROOT_Y,
    rowTops,
    TITLE_SLOT_HEIGHT,
    titleEdge,
    ZONE_LEFT_OFFSET,
    ZONE_ROW_GAP,
    ZONE_ROWS,
    ZONE_TOP_OFFSET,
    ZONE_WIDTH,
} from "./graphLayout";

const CENTER_ID = "value-focus";

const zoneTitleNode = ({
    cluster,
    index,
    centerX,
    tops,
}: {
    cluster: Cluster;
    index: number;
    centerX: number;
    tops: [number, number];
}): Node<ExplodeNodeData> => {
    const col = Math.floor(index / ZONE_ROWS);
    const row = index % ZONE_ROWS;
    return graphNode(
        `ttl-${cluster.field}`,
        centerX + ZONE_LEFT_OFFSET + col * ZONE_WIDTH,
        tops[row],
        {
            label: cluster.label,
            role: NodeRole.TITLE,
            count: cluster.distinctCount,
            title: true,
            overlay: { field: cluster.field, label: cluster.label },
            selection: [],
        },
        OVERLAY_Z,
    );
};

export const buildBaseGraph = ({
    categories,
    categoryField,
    categoryLabel,
    rootLabel,
    gridPage = 0,
    selected,
    clusters,
    subLoading = false,
    fieldOptions,
}: BuildFacetGraphParams): ExplodeFlow => {
    if (fieldOptions && !selected) {
        return buildFieldGrid({ fieldOptions, rootLabel, gridPage });
    }

    if (!selected) {
        return buildFieldFocus({ categories, categoryField, categoryLabel, rootLabel });
    }

    const stepX = NODE_WIDTH + COLUMN_GAP;
    const rootNode = graphNode("root", 0, ROOT_Y, {
        label: rootLabel,
        role: NodeRole.ROOT,
        selection: [],
    });
    const valueFocusNode = graphNode(
        CENTER_ID,
        stepX,
        ROOT_Y,
        {
            label: selected.value,
            caption: categoryLabel,
            count: categories.find((bucket) => bucket.value === selected.value)?.count,
            role: NodeRole.GROUP,
            focused: true,
            selection: [selected],
        },
        OVERLAY_Z,
    );
    const centerX = stepX;
    const centerY = ROOT_Y;
    const rootEdge = titleEdge("root", CENTER_ID, "e-root-value");

    if (subLoading && clusters.length === 0) {
        const ghostRowTops: [number, number] = [
            centerY + ZONE_TOP_OFFSET,
            centerY + ZONE_TOP_OFFSET + TITLE_SLOT_HEIGHT + ZONE_ROW_GAP,
        ];
        const ghostNodes = Array.from({ length: GHOST_COUNT }, (_, index) =>
            graphNode(
                `g-${index}`,
                centerX + ZONE_LEFT_OFFSET + Math.floor(index / GHOST_ZONE_SLOTS) * ZONE_WIDTH,
                ghostRowTops[index % GHOST_ZONE_SLOTS],
                {
                    label: "",
                    role: NodeRole.GROUP,
                    selection: [],
                    skeleton: true,
                },
                OVERLAY_Z,
            ),
        );
        const ghostEdges = ghostNodes.map((ghost, index) =>
            titleEdge(CENTER_ID, ghost.id, `e-ghost-${index}`),
        );
        return {
            nodes: [rootNode, valueFocusNode, ...ghostNodes],
            edges: [rootEdge, ...ghostEdges],
        };
    }

    const tops = rowTops(centerY);
    const titleNodes = clusters.map((cluster, index) =>
        zoneTitleNode({ cluster, index, centerX, tops }),
    );
    const zoneEdges = titleNodes.map((titleNode, index) =>
        titleEdge(CENTER_ID, titleNode.id, `e-ttl-${index}`),
    );

    return {
        nodes: [rootNode, valueFocusNode, ...titleNodes],
        edges: [rootEdge, ...zoneEdges],
    };
};
